import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  ListItemButton,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Flight as FlightIcon,
  Luggage as LuggageIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tripsAPI, wardrobeAPI, outfitsAPI, collectionsAPI } from '../services/api';
import { useUI } from '../contexts/UIContext';
import { Trip, WardrobeItem, Outfit, OutfitCollection } from '../types';

const TripPlanner: React.FC = () => {
  const { toast, confirm } = useUI();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [packingListOpen, setPackingListOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wardrobeItems, setWardrobeItems] = useState<WardrobeItem[]>([]);
  const [allOutfits, setAllOutfits] = useState<Outfit[]>([]);
  const [allCollections, setAllCollections] = useState<OutfitCollection[]>([]);
  const [packing, setPacking] = useState<{ items: any[]; outfits: any[]; collections: any[] } | null>(null);
  const [picker, setPicker] = useState<null | 'items' | 'outfits' | 'collections'>(null);

  // Form state
  const [tripName, setTripName] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tripType, setTripType] = useState('vacation');
  const [climate, setClimate] = useState('temperate');
  const [activities, setActivities] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const steps = ['Trip Details', 'Activities & Climate', 'Generate Packing List'];

  const activityOptions = [
    'business', 'sightseeing', 'beach', 'hiking', 'formal_dinner',
    'casual_dining', 'nightlife', 'sports', 'swimming', 'shopping'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tripsRes, itemsRes, outfitsRes, collectionsRes] = await Promise.all([
        tripsAPI.getTrips(),
        wardrobeAPI.getItems(),
        outfitsAPI.getOutfits(),
        collectionsAPI.getCollections(),
      ]);

      setTrips(tripsRes.data);
      setWardrobeItems(itemsRes.data?.items || itemsRes.data || []);
      setAllOutfits(outfitsRes.data || []);
      setAllCollections(collectionsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateTrip = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEditTrip = (trip: Trip) => {
    setSelectedTrip(trip);
    setTripName(trip.name);
    setDestination(trip.destination || '');
    setStartDate(trip.start_date);
    setEndDate(trip.end_date);
    setTripType(trip.trip_type || 'vacation');
    setClimate(trip.climate || 'temperate');
    setActivities(trip.activities || []);
    setNotes(trip.notes || '');
    setDialogOpen(true);
  };

  const handleSaveTrip = async () => {
    setLoading(true);
    try {
      const tripData = {
        name: tripName,
        destination,
        start_date: startDate,
        end_date: endDate,
        trip_type: tripType,
        climate,
        activities,
        notes,
        generate_plan: activeStep === 2,
      };

      if (selectedTrip) {
        await tripsAPI.updateTrip(selectedTrip.id, tripData);
      } else {
        await tripsAPI.createTrip(tripData);
      }

      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving trip:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrip = async (tripId: number) => {
    const ok = await confirm({
      title: 'Delete this trip?',
      message: 'Packing lists and outfit suggestions for this trip will be removed.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await tripsAPI.deleteTrip(tripId);
      fetchData();
      toast('Trip deleted', 'success');
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast('Failed to delete trip', 'error');
    }
  };

  const handleGeneratePackingList = async (tripId: number) => {
    setLoading(true);
    try {
      await tripsAPI.generatePlan(tripId);
      const tRes = await tripsAPI.getTrip(tripId);
      fetchData();
      await handleViewPackingList(tRes.data);
    } catch (error) {
      console.error('Error generating packing list:', error);
      toast('Failed to generate the packing list.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPackingList = async (trip: Trip) => {
    setSelectedTrip(trip);
    setPacking(null);
    setPackingListOpen(true);
    try {
      const res = await tripsAPI.getPackingList(trip.id);
      setPacking(res.data);
    } catch (error) {
      console.error('Error loading packing list:', error);
    }
  };

  // Persist edits to the packing list / attached outfits & collections
  const persistPlan = async (next: { items?: any[]; outfits?: any[]; collections?: any[] }) => {
    if (!selectedTrip) return;
    const items = next.items ?? packing?.items ?? [];
    const outfits = next.outfits ?? packing?.outfits ?? [];
    const collections = next.collections ?? packing?.collections ?? [];
    setPacking({ items, outfits, collections });
    try {
      await tripsAPI.updatePlan(selectedTrip.id, {
        item_ids: items.map((i: any) => i.id),
        outfit_ids: outfits.map((o: any) => o.id),
        collection_ids: collections.map((c: any) => c.id),
      });
    } catch (error) {
      console.error('Error saving plan:', error);
      toast('Could not save your changes.', 'error');
    }
  };

  const resetForm = () => {
    setSelectedTrip(null);
    setTripName('');
    setDestination('');
    setStartDate('');
    setEndDate('');
    setTripType('vacation');
    setClimate('temperate');
    setActivities([]);
    setNotes('');
    setActiveStep(0);
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const toggleActivity = (activity: string) => {
    setActivities(prev =>
      prev.includes(activity)
        ? prev.filter(a => a !== activity)
        : [...prev, activity]
    );
  };

  const calculateTripDuration = (start: string, end: string) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Trip Planner</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTrip}
        >
          Plan New Trip
        </Button>
      </Box>

      <Grid container spacing={3}>
        {trips.map((trip) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={trip.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FlightIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    {trip.name}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Destination:</strong> {trip.destination || 'Not specified'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Dates:</strong> {format(new Date(trip.start_date), 'MMM d')} - {format(new Date(trip.end_date), 'MMM d, yyyy')}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Duration:</strong> {calculateTripDuration(trip.start_date, trip.end_date)} days
                </Typography>
                
                <Box sx={{ mt: 1 }}>
                  <Chip label={trip.trip_type} size="small" sx={{ mr: 0.5 }} />
                  <Chip label={trip.climate} size="small" color="secondary" />
                </Box>
                
                {trip.packing_list && (
                  <Box sx={{ mt: 2 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 'small', mr: 0.5 }} />
                    <Typography variant="body2" component="span" color="success.main">
                      Packing list generated
                    </Typography>
                  </Box>
                )}
              </CardContent>
              
              <CardActions>
                {trip.packing_list ? (
                  <Button size="small" onClick={() => handleViewPackingList(trip)}>
                    View Packing List
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() => handleGeneratePackingList(trip.id)}
                    disabled={loading}
                  >
                    Generate Packing List
                  </Button>
                )}
                <IconButton size="small" onClick={() => handleEditTrip(trip)}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteTrip(trip.id)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Trip Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTrip ? 'Edit Trip' : 'Plan New Trip'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Trip Name"
                  value={tripName}
                  onChange={(e) => setTripName(e.target.value)}
                  fullWidth
                  required
                />
                
                <TextField
                  label="Destination"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  fullWidth
                />
                
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required
                />
                
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required
                />
                
                <FormControl fullWidth>
                  <InputLabel>Trip Type</InputLabel>
                  <Select
                    value={tripType}
                    label="Trip Type"
                    onChange={(e) => setTripType(e.target.value)}
                  >
                    <MenuItem value="vacation">Vacation</MenuItem>
                    <MenuItem value="business">Business</MenuItem>
                    <MenuItem value="mixed">Mixed (Business & Leisure)</MenuItem>
                    <MenuItem value="adventure">Adventure</MenuItem>
                    <MenuItem value="romantic">Romantic Getaway</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {activeStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Climate</InputLabel>
                  <Select
                    value={climate}
                    label="Climate"
                    onChange={(e) => setClimate(e.target.value)}
                  >
                    <MenuItem value="tropical">Tropical</MenuItem>
                    <MenuItem value="temperate">Temperate</MenuItem>
                    <MenuItem value="cold">Cold</MenuItem>
                    <MenuItem value="desert">Desert</MenuItem>
                    <MenuItem value="mixed">Mixed</MenuItem>
                  </Select>
                </FormControl>
                
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Planned Activities
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {activityOptions.map((activity) => (
                      <Chip
                        key={activity}
                        label={activity.replace('_', ' ')}
                        onClick={() => toggleActivity(activity)}
                        color={activities.includes(activity) ? 'primary' : 'default'}
                        variant={activities.includes(activity) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </Box>
                
                <TextField
                  label="Additional Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Based on your trip details, we'll generate a customized packing list and daily outfit suggestions.
                </Alert>
                
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Trip Summary
                  </Typography>
                  <Typography variant="body2">
                    <strong>Destination:</strong> {destination || 'Not specified'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong> {calculateTripDuration(startDate, endDate)} days
                  </Typography>
                  <Typography variant="body2">
                    <strong>Climate:</strong> {climate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Activities:</strong> {activities.join(', ') || 'None specified'}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={activeStep === 0 && (!tripName || !startDate || !endDate)}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSaveTrip}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Trip & Generate Plan'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Packing List Dialog */}
      <Dialog open={packingListOpen} onClose={() => setPackingListOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LuggageIcon sx={{ mr: 1 }} />
            Packing List - {selectedTrip?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {!packing ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Box>
              {/* Packing items */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">Packing list ({packing.items.length})</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setPicker('items')}>Add items</Button>
              </Box>
              {packing.items.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>No items yet. Use “Add items” or regenerate.</Alert>
              ) : (
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {packing.items.map((item: any) => (
                    <Grid size={{ xs: 6, sm: 4 }} key={item.id}>
                      <Card variant="outlined" sx={{ position: 'relative' }}>
                        <IconButton size="small" onClick={() => persistPlan({ items: packing.items.filter((i: any) => i.id !== item.id) })}
                          sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        {item.image_url && (
                          <CardMedia component="img" image={item.image_url} alt={item.name}
                            sx={{ height: 110, objectFit: 'cover', objectPosition: 'center top', bgcolor: '#f5f5f5' }} />
                        )}
                        <Box sx={{ p: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap display="block">{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">{item.subcategory || item.category}</Typography>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Attached outfits */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 2 }}>
                <Typography variant="subtitle1">Outfits ({packing.outfits.length})</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setPicker('outfits')}>Add outfits</Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {packing.outfits.length === 0
                  ? <Typography variant="body2" color="text.secondary">No outfits attached.</Typography>
                  : packing.outfits.map((o: any) => (
                    <Chip key={o.id} label={o.name} onDelete={() => persistPlan({ outfits: packing.outfits.filter((x: any) => x.id !== o.id) })} />
                  ))}
              </Box>

              {/* Attached collections */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">Collections ({packing.collections.length})</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setPicker('collections')}>Add collections</Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {packing.collections.length === 0
                  ? <Typography variant="body2" color="text.secondary">No collections attached.</Typography>
                  : packing.collections.map((c: any) => (
                    <Chip key={c.id} label={c.name} onDelete={() => persistPlan({ collections: packing.collections.filter((x: any) => x.id !== c.id) })} />
                  ))}
              </Box>

              {/* Per-day outfit suggestions */}
              {(packing as any).daily && (packing as any).daily.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Daily outfit suggestions</Typography>
                  {(packing as any).daily.map((day: any) => (
                    <Paper key={day.date} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 16 }} />
                        {format(new Date(day.date + 'T00:00:00'), 'EEE, MMM d')}
                        {day.event && day.event !== 'Day outfit' && (
                          <Chip label={day.event} size="small" sx={{ ml: 0.5, height: 20 }} />
                        )}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        {day.items.length === 0
                          ? <Typography variant="caption" color="text.secondary">No suggestion.</Typography>
                          : day.items.map((it: any) => (
                            <Box key={it.id} sx={{ width: 56, textAlign: 'center' }}>
                              {it.image_url
                                ? <Box component="img" src={it.image_url} alt={it.name}
                                    sx={{ width: 56, height: 56, objectFit: 'cover', objectPosition: 'center top', borderRadius: 1, border: '1px solid rgba(0,0,0,0.08)' }} />
                                : <Avatar variant="rounded" sx={{ width: 56, height: 56 }}>{it.name.charAt(0)}</Avatar>}
                              <Typography variant="caption" noWrap sx={{ display: 'block' }}>{it.name}</Typography>
                            </Box>
                          ))}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTrip && (
            <Button onClick={() => handleGeneratePackingList(selectedTrip.id)} disabled={loading}>Regenerate</Button>
          )}
          <Button onClick={() => setPackingListOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Add-items / outfits / collections picker */}
      <Dialog open={!!picker} onClose={() => setPicker(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {picker === 'items' ? 'Add items to pack' : picker === 'outfits' ? 'Add outfits' : 'Add collections'}
        </DialogTitle>
        <DialogContent>
          <List dense>
            {picker === 'items' && wardrobeItems
              .filter((it) => !(packing?.items || []).some((p: any) => p.id === it.id))
              .map((it) => (
                <ListItemButton key={it.id} onClick={() => persistPlan({ items: [...(packing?.items || []), it] })}>
                  <ListItemAvatar>{it.image_url ? <Avatar src={it.image_url} /> : <Avatar>{it.name.charAt(0)}</Avatar>}</ListItemAvatar>
                  <ListItemText primary={it.name} secondary={it.subcategory || it.category} />
                  <AddIcon fontSize="small" color="action" />
                </ListItemButton>
              ))}
            {picker === 'outfits' && allOutfits
              .filter((o) => !(packing?.outfits || []).some((p: any) => p.id === o.id))
              .map((o) => (
                <ListItemButton key={o.id} onClick={() => persistPlan({ outfits: [...(packing?.outfits || []), { id: o.id, name: o.name, thumbnail_url: o.thumbnail_url }] })}>
                  <ListItemAvatar>{o.thumbnail_url ? <Avatar src={o.thumbnail_url} /> : <Avatar>{o.name.charAt(0)}</Avatar>}</ListItemAvatar>
                  <ListItemText primary={o.name} secondary={o.occasion || ''} />
                  <AddIcon fontSize="small" color="action" />
                </ListItemButton>
              ))}
            {picker === 'collections' && allCollections
              .filter((c) => !(packing?.collections || []).some((p: any) => p.id === c.id))
              .map((c) => (
                <ListItemButton key={c.id} onClick={() => persistPlan({ collections: [...(packing?.collections || []), { id: c.id, name: c.name }] })}>
                  <ListItemText primary={c.name} secondary={`${(c.outfit_ids || []).length} outfits`} />
                  <AddIcon fontSize="small" color="action" />
                </ListItemButton>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPicker(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TripPlanner;